import { useState, useRef, useEffect } from 'react';
import { StarRating, PlatformIcon, normalizeReviewData, VerificationIcon } from './SharedComponents';

// Helper function to check if a string is a valid image URL
const isValidImageUrl = (url) => {
    if (!url || typeof url !== 'string') return false;

    // Check if it's a data URI image
    if (url.startsWith('data:image/')) return true;

    // Check if it's a proper HTTP/HTTPS URL
    const isUrl = url.startsWith('http://') || url.startsWith('https://');
    if (!isUrl) return false;

    // Check for common image extensions
    const imageExtensions = /\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?.*)?$/i;
    if (imageExtensions.test(url)) return true;

    // Check for known image hosting services (more comprehensive list)
    const imageHosts = new RegExp('(amazonaws\\.com|cloudfront\\.net|googleapis\\.com|googleusercontent\\.com|gravatar\\.com|unsplash\\.com|imgur\\.com|cloudinary\\.com|imagekit\\.io|uploadcare\\.com|cloudflare\\.com/[^/]*/image|facebook\\.com/[^/]*/picture|graph\\.facebook\\.com|fbcdn\\.net|twimg\\.com|linkedin\\.com/[^/]*/image|bing\\.com/[^/]*/image)', 'i');
    if (imageHosts.test(url)) return true;

    // For any other URL that might be an image, we'll try to load it
    // This is more permissive but safer for user-generated content
    return true;
};

// Helper function to generate initials from name
const generateInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase().slice(0, 2);
};

const ReviewsModal = ({ isOpen, onClose, reviews = [], averageRating = 4.8, totalReviews = 0, uniquePlatforms = [], locationData = [], accentColor = '#4A90E2' }) => {
    const [activeTab, setActiveTab] = useState('all');
    const [expandedReviews, setExpandedReviews] = useState(new Set());
    const [showReviewForm, setShowReviewForm] = useState(false);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const tabsContainerRef = useRef(null);

    // Filter reviews based on active tab
    const getFilteredReviews = () => {
        if (activeTab === 'all') return reviews;
        return reviews.filter(review => {
            const platform = review.platform?.name || review.platform;
            return platform.toLowerCase() === activeTab.toLowerCase();
        });
    };

    const filteredReviews = getFilteredReviews();

    // Get platform-specific rating for tabs
    const getPlatformRating = (platformName) => {
        const platformReviews = reviews.filter(review => {
            const platform = review.platform?.name || review.platform;
            return platform.toLowerCase() === platformName.toLowerCase();
        });
        if (platformReviews.length === 0) return 0;
        return platformReviews.reduce((sum, review) => sum + (review.rating || 0), 0) / platformReviews.length;
    };

    // Toggle expanded state for reviews
    const toggleExpanded = (reviewId) => {
        const newExpanded = new Set(expandedReviews);
        if (newExpanded.has(reviewId)) {
            newExpanded.delete(reviewId);
        } else {
            newExpanded.add(reviewId);
        }
        setExpandedReviews(newExpanded);
    };

    // Truncate text for collapsed reviews
    const truncateText = (text, limit = 150) => {
        if (!text || text.length <= limit) return text;
        return text.substring(0, limit) + '...';
    };

    // Check scroll position and update arrow visibility
    const checkScrollPosition = () => {
        if (tabsContainerRef.current) {
            const container = tabsContainerRef.current;
            setCanScrollLeft(container.scrollLeft > 0);
            setCanScrollRight(container.scrollLeft < container.scrollWidth - container.clientWidth);
        }
    };

    // Scroll tabs left/right
    const scrollTabs = (direction) => {
        if (tabsContainerRef.current) {
            const scrollAmount = 200;
            const newScrollLeft = direction === 'left'
                ? tabsContainerRef.current.scrollLeft - scrollAmount
                : tabsContainerRef.current.scrollLeft + scrollAmount;

            tabsContainerRef.current.scrollTo({
                left: newScrollLeft,
                behavior: 'smooth'
            });
        }
    };

    // Handle write review action
    const handleWriteReview = () => {
        setShowReviewForm(true);
    };

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setShowReviewForm(false);
            setActiveTab('all');
            setExpandedReviews(new Set());
        }
    }, [isOpen]);

    // Check scroll position on mount and platform changes
    useEffect(() => {
        if (isOpen) {
            setTimeout(checkScrollPosition, 100);
        }
    }, [isOpen, uniquePlatforms]);

    // Early return after all hooks are declared
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Custom scrollbar hiding styles */}
            <style>{`
                .scrollbar-hide {
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                }
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
            `}</style>

            {/* Backdrop */}
            <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>

            {/* Modal */}
            <div className="relative min-h-screen flex items-center justify-center p-4 md:p-4 sm:p-0">
                <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] sm:max-h-screen sm:h-screen sm:rounded-none overflow-hidden">
                    {/* Write Review Platform Selection */}
                    {showReviewForm ? (
                        <div className="p-6 sm:p-4">
                            {/* Header */}
                            <div className="flex items-center justify-between mb-6 sm:mb-4 pb-4 sm:pb-3 border-b border-gray-200">
                                <div className="flex items-center space-x-4">
                                    <button
                                        onClick={() => setShowReviewForm(false)}
                                        className="text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                    </button>
                                    <h2 className="text-2xl font-bold text-gray-900">Write a Review</h2>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            {/* Platform Selection */}
                            <div className="max-w-2xl mx-auto text-center">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Choose a platform to write your review</h3>
                                <p className="text-gray-600 mb-8">Click on a platform below to be redirected to write your review</p>

                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-3">
                                    {uniquePlatforms.map((platform) => {
                                        // Get platform URL from location data (same logic as PlatformSelectionModal)
                                        const getPlatformUrl = (platformId) => {
                                            for (const location of locationData) {
                                                const platformData = location.platforms?.find(p => p.name === platformId);
                                                if (platformData && platformData.profileUrl) {
                                                    return platformData.profileUrl;
                                                }
                                            }
                                            return null;
                                        };

                                        const platformUrl = getPlatformUrl(platform.toLowerCase());

                                        return (
                                            <div key={platform} className="relative">
                                                {platformUrl ? (
                                                    <a
                                                        href={platformUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex flex-col items-center p-6 border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all duration-200 group"
                                                    >
                                                        <div className="mb-3">
                                                            <PlatformIcon platform={platform} className="w-12 h-12 group-hover:scale-110 transition-transform" />
                                                        </div>
                                                        <h4 className="font-medium text-gray-900 mb-1">{platform}</h4>
                                                        <p className="text-sm text-blue-600 group-hover:text-blue-700">Click here</p>
                                                    </a>
                                                ) : (
                                                    <div className="flex flex-col items-center p-6 border border-gray-200 rounded-lg opacity-50 cursor-not-allowed">
                                                        <div className="mb-3">
                                                            <PlatformIcon platform={platform} className="w-12 h-12" />
                                                        </div>
                                                        <h4 className="font-medium text-gray-900 mb-1">{platform}</h4>
                                                        <p className="text-sm text-orange-600">No URL configured</p>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="mt-8 pt-6 border-t border-gray-200">
                                    <p className="text-sm text-gray-500">
                                        You will be redirected to the platform&apos;s website to write your review
                                    </p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Header Section */}
                            <div className="px-6 py-4 sm:px-4 sm:py-3 border-b border-gray-200">
                                <div className="flex items-center justify-between">
                                    <div className="flex-1 min-w-0">
                                        <h2 className="text-2xl sm:text-xl font-bold text-gray-900 mb-1 truncate">Customer Reviews</h2>
                                    </div>
                                    <button
                                        onClick={onClose}
                                        className="text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            {/* Tab Navigation */}
                            <div className="px-6 py-4 sm:px-4 sm:py-3 border-b border-gray-200">
                                <div className="relative flex items-center">
                                    {/* Left Arrow */}
                                    {canScrollLeft && (
                                        <button
                                            onClick={() => scrollTabs('left')}
                                            className="absolute left-0 z-10 p-2 rounded-full bg-white shadow-md hover:bg-gray-50 border"
                                            style={{ marginLeft: '-8px' }}
                                        >
                                            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                            </svg>
                                        </button>
                                    )}

                                    {/* Scrollable Tab Container */}
                                    <div
                                        ref={tabsContainerRef}
                                        className={`flex items-center space-x-6 overflow-x-auto scrollbar-hide ${canScrollLeft ? 'pl-12' : ''} ${canScrollRight ? 'pr-12' : ''}`}
                                        onScroll={checkScrollPosition}
                                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                                    >
                                        <button
                                            onClick={() => setActiveTab('all')}
                                            className={`flex items-center space-x-2 pb-2 whitespace-nowrap ${activeTab === 'all'
                                                ? 'border-b-2 border-blue-600 text-blue-600'
                                                : 'text-gray-600 hover:text-gray-800'
                                                }`}
                                        >
                                            <span className="font-medium">All Reviews {averageRating.toFixed(1)}</span>
                                        </button>

                                        {uniquePlatforms.map((platform) => {
                                            const platformRating = getPlatformRating(platform);

                                            return (
                                                <button
                                                    key={platform}
                                                    onClick={() => setActiveTab(platform.toLowerCase())}
                                                    className={`flex items-center space-x-2 pb-2 whitespace-nowrap ${activeTab === platform.toLowerCase()
                                                        ? 'border-b-2 border-blue-600 text-blue-600'
                                                        : 'text-gray-600 hover:text-gray-800'
                                                        }`}
                                                >
                                                    <PlatformIcon platform={platform} className="w-4 h-4" />
                                                    <span className="font-medium">
                                                        {platform} {platformRating > 0 && `${platformRating.toFixed(1)}`}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {/* Right Arrow */}
                                    {canScrollRight && (
                                        <button
                                            onClick={() => scrollTabs('right')}
                                            className="absolute right-0 z-10 p-2 rounded-full bg-white shadow-md hover:bg-gray-50 border"
                                            style={{ marginRight: '-8px' }}
                                        >
                                            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Content Area */}
                            <div className="flex flex-col sm:flex-col md:flex-row">
                                {/* Rating Summary Sidebar */}
                                <div className="w-full md:w-80 bg-gray-50 p-6 sm:p-4 border-r border-gray-200 sm:border-r-0 sm:border-b">
                                    <div className="text-center">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Overall Rating</h3>
                                        <div className="text-4xl font-bold text-gray-900 mb-2">
                                            {averageRating.toFixed(1)}
                                        </div>
                                        <div className="flex justify-center mb-2">
                                            <StarRating
                                                rating={Math.round(averageRating)}
                                                size="w-6 h-6"
                                                color={accentColor}
                                            />
                                        </div>
                                        <p className="text-gray-600 mb-4">({totalReviews.toLocaleString()})</p>
                                        <button
                                            onClick={handleWriteReview}
                                            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                                        >
                                            Write a review
                                        </button>
                                    </div>
                                </div>

                                {/* Reviews List */}
                                <div className="flex-1 p-6 sm:p-4 overflow-y-auto max-h-[60vh] sm:max-h-none sm:flex-1">
                                    <div className="space-y-6">
                                        {filteredReviews.map((review, index) => {
                                            const normalized = normalizeReviewData(review);
                                            const reviewId = normalized.id || index;
                                            const isExpanded = expandedReviews.has(reviewId);
                                            const shouldTruncate = normalized.comment && normalized.comment.length > 150;

                                            return (
                                                <div key={reviewId} className="border-b border-gray-200 pb-6 last:border-b-0">
                                                    {/* Star Rating */}
                                                    <div className="flex items-center mb-3">
                                                        <StarRating rating={normalized.rating} size="w-5 h-5" color={accentColor} />
                                                    </div>

                                                    {/* Review Text */}
                                                    <div className="mb-4">
                                                        <p className="text-gray-700 leading-relaxed">
                                                            {isExpanded || !shouldTruncate
                                                                ? normalized.comment
                                                                : truncateText(normalized.comment)
                                                            }
                                                        </p>
                                                        {shouldTruncate && (
                                                            <button
                                                                onClick={() => toggleExpanded(reviewId)}
                                                                className="text-sm font-medium mt-2 transition-colors"
                                                                style={{ color: accentColor }}
                                                            >
                                                                {isExpanded ? 'Read less' : 'Read more'}
                                                            </button>
                                                        )}
                                                    </div>

                                                    {/* User Profile */}
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center space-x-3">
                                                            {/* Profile Image */}
                                                            <div className="w-10 h-10 rounded-full relative">
                                                                {isValidImageUrl(normalized.avatar) ? (
                                                                    <>
                                                                        <img
                                                                            src={normalized.avatar}
                                                                            alt={normalized.name}
                                                                            className="w-10 h-10 rounded-full object-cover"
                                                                            onError={(e) => {
                                                                                e.target.style.display = 'none';
                                                                                e.target.nextElementSibling.style.display = 'flex';
                                                                            }}
                                                                        />
                                                                        <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium absolute top-0 left-0 hidden">
                                                                            {generateInitials(normalized.name)}
                                                                        </div>
                                                                    </>
                                                                ) : (
                                                                    <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
                                                                        {normalized.avatar && !isValidImageUrl(normalized.avatar) ? normalized.avatar : generateInitials(normalized.name)}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div>
                                                                <div className="flex items-center space-x-2">
                                                                    <span className="font-medium text-gray-900">{normalized.name}</span>
                                                                    {normalized.isVerified && (
                                                                        <VerificationIcon size="16" />
                                                                    )}
                                                                </div>
                                                                <p className="text-sm text-gray-500">{normalized.date}</p>
                                                            </div>
                                                        </div>

                                                        {/* Platform Logo */}
                                                        <PlatformIcon platform={normalized.platform} className="w-6 h-6" />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {filteredReviews.length === 0 && (
                                        <div className="text-center py-12">
                                            <div className="text-gray-400 mb-2">
                                                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10m0 0V6a2 2 0 00-2-2H9a2 2 0 00-2 2v2m0 0v10a2 2 0 002 2h6a2 2 0 002-2" />
                                                </svg>
                                            </div>
                                            <p className="text-gray-500">No reviews found for this platform</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReviewsModal; 